/* eslint-disable max-lines */
import {
  DownCircleOutlined,
  FilterOutlined,
  HighlightOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  StarOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { countHankakuAndZenkaku } from '@salescore/buff-common'
import type { PropertyTypeEnum, ViewFieldsFragment } from '@salescore/client-api'
import { HUB_PROPERTY_TYPE_ICONS, KpiIcon } from '@salescore/client-common'
import {
  type CompiledKpiPivotViewConfig,
  CORE_CONSTANT,
  type ViewQueryRecordNode,
  type ViewQueryRecordNodeWithId,
  type ViewUiKpiPivot,
} from '@salescore/core'
import { Space, Table, Tooltip } from 'antd'
import type { HashMap } from 'effect'
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilState } from 'recoil'

import type {
  PivotColumn,
  PivotRowColumn,
  PivotSystemColumn,
  RSheetColumn,
  RSheetRecordNode,
} from '../../../domain/column'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes.ts'
import { useProgress } from '../../../hooks/useProgress.ts'
import { useViewQueryResult } from '../../../hooks/useViewQueryResult'
import { extractKpiId } from '../../../kpi/utility.ts'
import type { KpiPivotTableContext } from '../../../recoil/context.ts'
import { useGetInnerCells, useRowExpandable } from '../../../recoil/cursor.ts'
import { kpiParameterAtom } from '../../../recoil/navigation/atoms.ts'
import { useCalculatedRowHeight, useNavigationModal, useSetRowConfig } from '../../../recoil/navigation/hooks'
import type { LatestFieldChangeItem } from '../../../recoil/recordsHistory.ts'
import { autoDrillDownParameterAtom, type SalescoreObjectAbility, type ViewAbility } from '../../../recoil/view/atoms'
import { useResizeColumnMutation } from '../../../recoil/viewConfig.ts'
import { MINIMUM_WIDTH, ROW_HEIGHT } from '../../../rsheet/components/body/const.tsx'
import { DraggableBar } from '../../../rsheet/components/header/DraggableBar.tsx'
import { DraggableHeaderColumn } from '../../../rsheet/components/header/DraggableHeaderColumn.tsx'
import { KpiHeaderContextMenu } from '../../../rsheet/components/header/HeaderContextMenu.tsx'
import {
  type BlockHeaderStyle,
  DEFAULT_HEADER_COLOR,
} from '../../../rsheet/components/header/RSheetsNodeBlockHeader.tsx'
import { RSheet } from '../../../rsheet/components/RSheet'
import {
  type SetMainCursorByRecordIdArguments,
  useSetMainCursorByRecordId,
} from '../../../rsheet/hooks/useSetMainCursorByRecordId.ts'
import { RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'
import { KpiPivotTableCellGroup } from './KpiPivotTableCellGroup.tsx'

const BLOCK_HEADER_STYLE: BlockHeaderStyle = {
  treeHeaderHeightPerNode: 42,
  blockStyle: {
    paddingLeft: 8,
    paddingTop: 12,
    fontSize: 14,
  },
  renderCellLabel: (cell) => cell.label,
}

export const ViewUIKpiPivotBody = ({
  component,
  columns,
  compiledConfig,
  view,
  ability,
  setHasChangeToViewSchema,
}: {
  component: ViewUiKpiPivot
  columns: Array<
    // eslint-disable-next-line sonarjs/use-type-alias
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  compiledConfig: CompiledKpiPivotViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const setRowConfig = useSetRowConfig()
  const rowHeight = useCalculatedRowHeight(compiledConfig)
  const getInnerCells = useGetInnerCells(undefined)
  const rowExpandable = useRowExpandable(columns, undefined)
  const progress = useProgress(compiledConfig)
  const queryResult = useViewQueryResult(view)
  const [autoDrillDownParameter, setAutoDrillDownParameter] = useRecoilState(autoDrillDownParameterAtom)
  const { drillDownModal } = useNavigationModal()
  const isEmptyTable = queryResult !== undefined && columns.length === 0

  useEffect(() => {
    if (autoDrillDownParameter !== undefined) {
      drillDownModal.showModal(autoDrillDownParameter)
      setAutoDrillDownParameter(undefined)
    }
  }, [columns])

  const headerWithBigIcon =
    // component.pivot.columns.length === 1 &&
    component.pivot.columns.at(-1)?.key === CORE_CONSTANT.KPI_PIVOT_KPI_ID_COLUMN_NAME

  const context = useMemo(
    () => ({
      type: 'kpiPivot' as const,
      kpis: component.kpis,
      pivot: component.pivot,
      headerWithBigIcon,
      headerTitleRowNum: headerWithBigIcon
        ? Math.max(
            ...columns.map((x) =>
              calculateHeaderTitleRowNumber(
                x.columnType === 'pivot_row' || x.columnType === 'pivot' ? x.title : '',
                x.width,
              ),
            ),
          )
        : 1,
      fixedLeftColumnIndex: component.pivot.rows.length + 1,
    }),
    [component, columns, headerWithBigIcon],
  )
  const setMainCursorByRecordId = useSetMainCursorByRecordId(
    context.fixedLeftColumnIndex,
    columns,
    compiledConfig,
    ability,
    undefined,
    undefined,
  )
  const renderHeaderCell = useCallback(
    (
      column:
        PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>,
      columnIndex: number,
    ) => (
      <KpiPivotHeaderCell
        column={column}
        columnIndex={columnIndex}
        columns={columns}
        context={context}
        compiledConfig={compiledConfig}
        view={view}
        setHasChangeToViewSchema={setHasChangeToViewSchema}
      />
    ),
    [columns, context, compiledConfig, view],
  )
  const renderCellGroup = useCallback(
    ({
      row,
      column,
      rowIndex,
      columnIndex,
      expandable,
      expanded,
      latestFieldChangesRow,
      setDragging,
      setMainCursorByRecordId,
      expandOnDrag,
    }: {
      row: ViewQueryRecordNodeWithId
      column:
        PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
      rowIndex: number
      columnIndex: number
      expandable: boolean
      expanded: boolean
      latestFieldChangesRow: HashMap.HashMap<string, LatestFieldChangeItem>
      setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
      setDragging: () => void
      expandOnDrag: (position: RSheetsCursorPosition) => void
    }): ReactElement => (
      <KpiPivotTableCellGroup
        row={row}
        column={column}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        getInnerCells={getInnerCells}
        expandable={expandable}
        expanded={expanded}
        latestFieldChangesRow={latestFieldChangesRow}
        rowHeight={rowHeight}
        setMainCursorByRecordId={setMainCursorByRecordId}
        setRowConfig={setRowConfig}
        view={view}
        setDragging={setDragging}
        expandOnDrag={expandOnDrag}
        context={context}
        columns={columns}
        rootRecordNode={row}
        progress={progress}
        ability={ability}
      />
    ),
    [ability, columns, context, getInnerCells, progress, rowHeight, setRowConfig, view],
  )

  return (
    <div className="h-full">
      {isEmptyTable ? (
        <Table />
      ) : (
        <RSheet
          // eslint-disable-next-line sonarjs/todo-tag
          height="100%" // TODO
          columns={columns}
          context={context}
          fixedFooter={() => null}
          footer={() => null}
          columnVirtualization={false}
          compiledConfig={compiledConfig}
          renderHeaderCell={renderHeaderCell}
          view={view}
          blockHeaderStyle={BLOCK_HEADER_STYLE}
          visibleRowNumber={undefined}
          rowExpandable={rowExpandable}
          renderCellGroup={renderCellGroup}
          setMainCursorByRecordId={setMainCursorByRecordId}
          rowHeight={rowHeight}
          ability={ability}
          query={undefined}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      )}
    </div>
  )
}

// かなりざっくり
const HANKAKU_CHAR_WIDTH = 7.5
const ZENKAKU_CHAR_WIDTH = 12.5

const calculateHeaderTitleRowNumber = (title: string, width: number): number => {
  const result = countHankakuAndZenkaku(title)
  const widthForTitle =
    result.hankakuChars.length * HANKAKU_CHAR_WIDTH + result.zenkakuChars.length * ZENKAKU_CHAR_WIDTH
  return Math.ceil(widthForTitle / width)
}

const KpiPivotHeaderCell = ({
  column,
  columnIndex,
  columns,
  context,
  compiledConfig,
  view,
  setHasChangeToViewSchema,
}: {
  column:
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  columnIndex: number
  columns: Array<
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  context: KpiPivotTableContext
  compiledConfig: CompiledKpiPivotViewConfig
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const resizeColumnMutation = useResizeColumnMutation(compiledConfig, view)
  const { width } = column
  const [openColumnContextMenu, setOpenColumnContextMenu] = useState(false)
  const [headerFocused, setHeaderFocused] = useState(false)
  const headerCellHeight = useMemo(
    () => (context.headerWithBigIcon ? 62 + (context.headerTitleRowNum - 1) * 14 : ROW_HEIGHT),
    [context],
  )

  const [kpiParameter] = useRecoilState(kpiParameterAtom)
  const kpiId = useMemo(() => extractKpiId(kpiParameter, column), [column, kpiParameter])
  const isCommitmentKpiLabeldKpi = kpiParameter.goalConfig?.commitmentKpiLabel?.viewId === kpiId

  // Tooltipの領域がコンテキストメニューまで含んでおり、コンテキストメニューを開いた際にフォーカスが残ったままになってしまうこと防ぐ
  const tooltipVisibility =
    column.columnType === 'pivot' &&
    column.helpText !== undefined &&
    column.helpText !== '' &&
    headerFocused &&
    !openColumnContextMenu

  return (
    <Tooltip
      title={column.columnType === 'pivot' ? column.helpText : undefined}
      placement="right"
      open={tooltipVisibility}
    >
      <div
        onMouseEnter={() => {
          setHeaderFocused(true)
        }}
        onMouseLeave={() => {
          setHeaderFocused(false)
        }}
        onDragOver={(e) => {
          e.preventDefault() // onDropを発火させるのに必要
        }}
        onDrop={(e) => {
          // onDropで特にロジックは書かないが、onDropの定義はドロップアニメーション(要素の元の位置に戻るアニメーション)をdisableするために必要
          e.preventDefault()
        }}
        style={{
          width,
          position: 'relative',
          transition: RSheetsStyle.cellTransition,
        }}
      >
        <DraggableHeaderColumn
          width={width}
          height={headerCellHeight}
          widths={columns.map((x) => x.width)}
          column={column}
          columnIndex={columnIndex}
          onClick={() => {
            setOpenColumnContextMenu(true)
            setHeaderFocused(false)
          }}
          onContextMenu={() => {
            setOpenColumnContextMenu(true)
            setHeaderFocused(false)
          }}
          columns={columns}
          compiledConfig={compiledConfig}
          view={view}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
        <div
          className="p-1"
          style={{
            borderRight: `1px ${DEFAULT_HEADER_COLOR.borderColor} solid`,
            color: openColumnContextMenu ? DEFAULT_HEADER_COLOR.textColorWhite : DEFAULT_HEADER_COLOR.textColor,
            backgroundColor: openColumnContextMenu ? DEFAULT_HEADER_COLOR.bgColorDark : DEFAULT_HEADER_COLOR.bgColor,
            padding: '0px 4px',
            // backgroundColor: '#F6F8F9',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            height: headerCellHeight - 2,
            ...(context.headerWithBigIcon
              ? {}
              : {
                  display: 'flex',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }),
          }}
        >
          <KpiPivotHeaderCellBody
            column={column}
            isCommitmentKpiLabeldKpi={isCommitmentKpiLabeldKpi}
            context={context}
          />
        </div>
        <DraggableBar
          width={width}
          height={headerCellHeight}
          setWidth={(width) => {
            resizeColumnMutation({ column, width, allColumnIndex: columnIndex, setHasChangeToViewSchema })
          }}
        />
        {openColumnContextMenu && (column.columnType === 'pivot_row' || column.columnType === 'pivot') && (
          <KpiHeaderContextMenu
            column={column}
            hide={() => {
              setOpenColumnContextMenu(false)
              setHeaderFocused(false)
            }}
            columns={columns}
            compiledConfig={compiledConfig}
            view={view}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />
        )}
      </div>
    </Tooltip>
  )
}

const KpiPivotHeaderCellBody = <T extends RSheetRecordNode>({
  column,
  isCommitmentKpiLabeldKpi,
  context,
}: {
  column: RSheetColumn<T>
  isCommitmentKpiLabeldKpi: boolean
  context: KpiPivotTableContext
}): ReactElement => {
  if (column.metaType === 'record_url') {
    return <></>
  }
  if (column.width === MINIMUM_WIDTH) {
    return <></>
  }
  const { headerWithBigIcon } = context

  return (
    <>
      {headerWithBigIcon && (
        <div
          style={{
            textAlign: 'center',
            fontSize: 24,
            marginBottom: 2,
            marginTop: 5,
          }}
        >
          <ColumnIcon column={column} />
        </div>
      )}
      <div
        className={
          headerWithBigIcon ? 'flex w-full items-center justify-center align-middle' : `flex pl-1 align-middle`
        }
      >
        <Space
          style={
            headerWithBigIcon
              ? {
                  margin: 0,
                  padding: 0,
                  fontSize: 11,
                  textAlign: 'center',
                }
              : {}
          }
        >
          <span className={`whitespace-normal break-all ${isCommitmentKpiLabeldKpi ? 'font-bold' : ''}`}>
            {column.title ?? ''}
            {column.required === true ? '*' : ''}
          </span>
          {column.isFiltered === true && <FilterOutlined style={{ opacity: 0.5 }} />}
          {column.sortState === 'asc' ? (
            <SortAscendingOutlined />
          ) : // eslint-disable-next-line sonarjs/no-nested-conditional
          column.sortState === 'desc' ? (
            <SortDescendingOutlined />
          ) : (
            <></>
          )}
          {column.isHighlighted === true && <HighlightOutlined style={{ opacity: 0.5 }} />}
        </Space>
      </div>
    </>
  )
}

const ColumnIcon = <T extends RSheetRecordNode>({ column }: { column: RSheetColumn<T> }): ReactElement => {
  if (column.icon !== undefined) {
    if (typeof column.icon === 'string') {
      return <KpiIcon imageName={column.icon} />
    }
    return column.icon
  }
  if (column.type === 'kpi') {
    return <StarOutlined />
  }

  switch (column.metaType) {
    case 'relation': {
      return <SearchOutlined />
    }
    case 'url': {
      return <LinkOutlined />
    }
    case 'phone_number': {
      return <PhoneOutlined />
    }
    case 'email': {
      return <MailOutlined />
    }
    case 'select': {
      return <DownCircleOutlined />
    }
    case 'multi_select': {
      return <UnorderedListOutlined />
    }
    default: {
      break
    }
  }
  if (column.type === 'notFound') {
    return <QuestionCircleOutlined />
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-unnecessary-condition
  const typeIcon = HUB_PROPERTY_TYPE_ICONS[(column.type as PropertyTypeEnum) ?? ('string' as const)]
  return <>{typeIcon}</>
}
